<p-dialog
    [header]="header()"
    [visible]="visible()"
    (visibleChange)="onVisibleChange($event)"
    [style]="{ width: '72rem', height: '88vh' }"
    [breakpoints]="{ '1280px': '95vw' }"
    [modal]="true"
    [closable]="phase() !== 'loading'"
    [draggable]="false"
    contentStyleClass="h-full"
    appendTo="body"
>
    <div class="flex flex-col gap-4 h-full min-h-0">
        @if (error(); as e) {
        <p-message
            severity="error"
            icon="pi pi-times-circle"
            styleClass="w-full"
        >
            <span class="text-sm">{{ e }}</span>
        </p-message>
        }

        <!-- form -->
        @if (phase() === 'form') {
        <div class="flex flex-col gap-4 flex-1 min-h-0 overflow-y-auto pr-1">
            <small class="text-muted-color">
                Generates base art for
                <b>{{ gameName() || 'this game' }}</b> in the selected site
                template's style. @if (style()) { Uses the template's style
                descriptor. } @else {
                <span class="text-amber-600"
                    >No template style available — the art will be
                    generic.</span
                >
                }
            </small>

            <div>
                <div class="block font-medium mb-1 text-sm">
                    Template references
                    <span class="font-normal text-muted-color"
                        >({{ references().length }})</span
                    >
                </div>
                @if (references().length > 0) {
                <div class="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    @for (ref of references(); track ref.dataUrl) {
                    <div
                        class="aspect-square rounded border border-surface-200 dark:border-surface-700 bg-surface-100 dark:bg-surface-800 overflow-hidden flex items-center justify-center p-1"
                    >
                        <img
                            [src]="ref.dataUrl"
                            alt="Template reference"
                            class="max-w-full max-h-full object-contain"
                        />
                    </div>
                    }
                </div>
                } @else {
                <small class="text-amber-600"
                    >This site template ships no brand references.</small
                >
                }
            </div>

            <div>
                <label class="block font-medium mb-1 text-sm">Variants</label>
                <p-selectbutton
                    [options]="sampleOptions"
                    optionLabel="label"
                    optionValue="value"
                    [ngModel]="samples()"
                    (ngModelChange)="samples.set($event ?? 4)"
                    [allowEmpty]="false"
                />
            </div>

            <div class="flex flex-col gap-2">
                <label class="block font-medium text-sm">
                    @if (fullManual()) { Full prompt
                    <span class="text-red-500">*</span> } @else { Add details
                    for the AI
                    <span class="font-normal text-muted-color">(optional)</span>
                    }
                </label>
                <textarea
                    pTextarea
                    class="w-full"
                    rows="4"
                    [placeholder]="
                        fullManual()
                            ? 'Write the complete image prompt (replaces AI authoring)'
                            : 'e.g. show the wheel mid-spin, gold rim, dramatic lighting'
                    "
                    [ngModel]="prompt()"
                    (ngModelChange)="prompt.set($event ?? '')"
                ></textarea>
                <div class="flex items-center gap-2">
                    <p-checkbox
                        inputId="art-full-manual"
                        [binary]="true"
                        [ngModel]="fullManual()"
                        (ngModelChange)="fullManual.set($event)"
                    />
                    <label
                        for="art-full-manual"
                        class="text-sm text-muted-color"
                        >Write the full prompt myself (replace AI
                        authoring)</label
                    >
                </div>
                <small class="text-muted-color">
                    @if (fullManual()) { Your prompt is sent as-is — the game
                    name/description are not auto-added. } @else { The AI
                    composes the game name + description + template style with
                    your details. The same input applies to every selected
                    asset. }
                </small>
            </div>

            @if (creditBlock(); as block) {
            <p-message severity="warn" icon="pi pi-wallet" styleClass="w-full">
                <span class="text-sm">{{ block }}</span>
            </p-message>
            }
        </div>
        <div class="flex justify-end shrink-0">
            <p-button
                label="Generate"
                [icon]="aiIcon"
                [disabled]="!canGenerate()"
                (click)="generate()"
            />
        </div>
        }

        <!-- loading -->
        @if (phase() === 'loading') {
        <div
            class="flex flex-1 flex-col items-center justify-center gap-3 py-10"
        >
            <p-progress-spinner
                styleClass="w-12 h-12"
                strokeWidth="4"
                ariaLabel="Generating"
            />
            <div class="text-sm text-muted-color">
                Generating {{ samples() }} variant(s) per asset — this can take
                up to a minute…
            </div>
        </div>
        }

        <!-- results -->
        @if (phase() === 'results') {
        <div class="flex flex-col gap-4 flex-1 min-h-0">
            <div class="text-sm text-muted-color shrink-0">
                Pick one candidate per asset, then apply — or refine the prompt
                and regenerate below.
            </div>

            <div
                class="flex flex-col gap-5 flex-1 min-h-0 overflow-y-auto pr-1"
            >
                @for (type of assetTypes(); track type) {
                <div class="flex flex-col gap-2">
                    <div class="font-medium text-sm capitalize">{{ type }}</div>
                    @if (candidatesFor(type).length === 0) {
                    <p-message severity="warn" styleClass="w-full">
                        <span class="text-sm"
                            >No candidates were returned for {{ type }}.</span
                        >
                    </p-message>
                    } @else {
                    <div
                        class="grid gap-3"
                        [ngClass]="
                            type === 'banner'
                                ? 'grid-cols-1 sm:grid-cols-2'
                                : 'grid-cols-2 sm:grid-cols-4'
                        "
                    >
                        @for (c of candidatesFor(type); track c.url) {
                        <button
                            type="button"
                            class="relative rounded border overflow-hidden flex flex-col text-left"
                            [class.border-primary]="isSelected(type, c.url)"
                            [class.ring-2]="isSelected(type, c.url)"
                            [class.ring-primary]="isSelected(type, c.url)"
                            [class.border-surface-200]="!isSelected(type, c.url)"
                            [class.dark:border-surface-700]="
                                !isSelected(type, c.url)
                            "
                            (click)="select(type, c.url)"
                        >
                            <div
                                class="flex items-center justify-center bg-surface-100 dark:bg-surface-800 p-2"
                                [class.aspect-square]="type === 'icon'"
                                [class.aspect-video]="type === 'banner'"
                            >
                                <img
                                    [src]="c.url"
                                    [alt]="type + ' candidate'"
                                    class="max-w-full max-h-full object-contain"
                                    referrerpolicy="no-referrer"
                                />
                                <i
                                    class="pi pi-search-plus absolute top-1 left-1 bg-black/50 text-white rounded p-1 text-xs"
                                    title="View large"
                                    (click)="
                                        preview.open(c.url, type);
                                        $event.stopPropagation()
                                    "
                                ></i>
                            </div>
                            @if (isSelected(type, c.url)) {
                            <div
                                class="flex items-center gap-1 px-2 py-1 text-primary text-xs"
                            >
                                <i class="pi pi-check"></i><span>Selected</span>
                            </div>
                            }
                        </button>
                        }
                    </div>
                    }
                </div>
                }
            </div>

            <div
                class="flex flex-col gap-2 pt-3 border-t border-surface-200 dark:border-surface-700 shrink-0"
            >
                <label class="font-medium text-sm">
                    @if (regenFullManual()) { Edit the full prompt and
                    regenerate } @else { Refine — add details and regenerate }
                    <span class="font-normal text-muted-color">(optional)</span>
                </label>
                <textarea
                    pTextarea
                    class="w-full"
                    rows="4"
                    [placeholder]="
                        regenFullManual()
                            ? 'Edit the full prompt'
                            : 'Add details and the AI recomposes — leave empty to re-roll with the same settings'
                    "
                    [ngModel]="regenPrompt()"
                    (ngModelChange)="regenPrompt.set($event ?? '')"
                ></textarea>
                <div class="flex items-center gap-2">
                    <p-checkbox
                        inputId="regen-full-manual"
                        [binary]="true"
                        [ngModel]="regenFullManual()"
                        (ngModelChange)="onRegenFullManualChange($event)"
                    />
                    <label
                        for="regen-full-manual"
                        class="text-sm text-muted-color"
                        >Write the full prompt myself (replace AI
                        authoring)</label
                    >
                </div>
                @if (creditBlock(); as block) {
                <p-message
                    severity="warn"
                    icon="pi pi-wallet"
                    styleClass="w-full"
                >
                    <span class="text-sm">{{ block }}</span>
                </p-message>
                }
                <div class="flex justify-between gap-2">
                    <p-button
                        label="Back to settings"
                        icon="pi pi-cog"
                        severity="secondary"
                        text
                        (click)="backToForm()"
                    />
                    <div class="flex gap-2">
                        <p-button
                            label="Regenerate"
                            icon="pi pi-refresh"
                            severity="secondary"
                            outlined
                            [disabled]="!canRegenerate()"
                            (click)="regenerate()"
                        />
                        <p-button
                            label="Apply"
                            icon="pi pi-check"
                            [disabled]="!canApply()"
                            (click)="apply()"
                        />
                    </div>
                </div>
            </div>
        </div>
        }
    </div>
</p-dialog>
